import React, { useState, useMemo } from 'react';
import { CalendarDays, ChevronDown, ChevronRight } from 'lucide-react';
import type { KeyDatesWidgetData } from '../../../types/agentWidgets';

interface Props {
  data: KeyDatesWidgetData;
}

const SIGNIFICANCE_STYLES: Record<string, { dot: string; bg: string }> = {
  CRITICAL: { dot: 'bg-red-500', bg: 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900/20' },
  HIGH: { dot: 'bg-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/20' },
  MODERATE: { dot: 'bg-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-900/10 border-cyan-200 dark:border-cyan-900/20' },
  LOW: { dot: 'bg-gray-300 dark:bg-gray-600', bg: 'bg-gray-50 dark:bg-gray-800/30 border-gray-200 dark:border-gray-700' },
};

const CATEGORY_COLORS: Record<string, string> = {
  Medical: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  Legal: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  Administrative: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

export const KeyDatesWidget: React.FC<Props> = ({ data }) => {
  const [collapsed, setCollapsed] = useState(false);

  const sorted = useMemo(() => {
    return [...data.dates].sort((a, b) => {
      const da = new Date(a.date).getTime();
      const db = new Date(b.date).getTime();
      return da - db;
    });
  }, [data.dates]);

  if (!data.dates || data.dates.length === 0) return null;

  return (
    <div className="bg-teal-50/30 dark:bg-teal-900/5 border-l-3 border-teal-400 dark:border-teal-600">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-teal-50/50 dark:hover:bg-teal-900/10 transition-colors"
      >
        {collapsed ? <ChevronRight className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400 flex-shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400 flex-shrink-0" />}
        <CalendarDays className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400 flex-shrink-0" />
        <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
          {data.title || 'Key Dates'}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
          {data.dates.length} date{data.dates.length !== 1 ? 's' : ''}
        </span>
      </button>

      {!collapsed && (
        <div className="px-3 pb-3 space-y-1.5">
          {sorted.map((item, i) => {
            const styles = SIGNIFICANCE_STYLES[item.significance] || SIGNIFICANCE_STYLES.LOW;
            const catColor = CATEGORY_COLORS[item.category] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';

            return (
              <div key={i} className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded border ${styles.bg}`}>
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${styles.dot}`} />
                <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400 whitespace-nowrap w-[80px] flex-shrink-0">
                  {formatDate(item.date)}
                </span>
                <span className="text-[11px] font-medium text-gray-800 dark:text-gray-200 flex-1 min-w-0">
                  {item.label}
                </span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${catColor}`}>
                  {item.category}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
